import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Empty, Spin, Tag, Typography, message } from 'antd';
import { roleService, Role } from '../services/roles';
import { permissionService, Permission } from '../services/permissions';

const { Title, Text } = Typography;

type PermissionNode = {
  id: string;
  label: string;
  depth: number;
  actionMap: Record<string, Permission>;
  children: PermissionNode[];
};

type PermissionMatrix = {
  actions: string[];
  tree: PermissionNode[];
};

const toLabel = (value: string) =>
  value
    .replace(/[-_.]+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());

const actionLabel = (value: string) => {
  if (value === 'update') return 'Edit';
  return toLabel(value);
};

const labelOverrides: Record<string, string> = {
  'projects.tasks': 'Task',
  'projects.letters': 'Letter',
  'projects.dispatch': 'Dispatch',
};

const getNodeLabel = (id: string, fallback: string) => labelOverrides[id] ?? fallback;

export const Roles: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(new Set());
  const [editingEnabled, setEditingEnabled] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!selectedRoleId && roles.length > 0) {
      const firstEditable = roles.find((role) => role.name !== 'owner') ?? roles[0];
      setSelectedRoleId(firstEditable.id);
    }
  }, [roles, selectedRoleId]);

  const selectedRole = useMemo(
    () => roles.find((role) => role.id === selectedRoleId) ?? null,
    [roles, selectedRoleId]
  );

  useEffect(() => {
    if (selectedRole) {
      setSelectedPermissionIds(selectedRole.permissions?.map((permission) => permission.id) ?? []);
    }
  }, [selectedRole?.id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesData, permissionsData] = await Promise.all([
        roleService.getAll(),
        permissionService.getAll(),
      ]);
      const visibleRoles = rolesData.filter(
        (role) => !['owner', 'admin', 'developer'].includes(role.name)
      );
      setRoles(visibleRoles);
      setPermissions(permissionsData);
    } catch {
      message.error('Failed to load roles and permissions');
    } finally {
      setLoading(false);
    }
  };

  const permissionMatrix: PermissionMatrix = useMemo(() => {
    const allowedActions = ['view', 'create', 'update', 'verify', 'delete'];
    const actionSet = new Set<string>();
    const rootMap = new Map<string, PermissionNode>();

    const sortedPermissions = [...permissions].sort((a, b) => {
      const normalizeRoot = (name: string) => {
        const parts = name.split('.');
        parts.pop();
        let path = parts.length > 0 ? parts : ['misc'];
        if (path[0] === 'dispatch') {
          path = ['projects', 'dispatch', ...path.slice(1)];
        }
        return path[0] ?? 'misc';
      };
      const rootA = normalizeRoot(a.name);
      const rootB = normalizeRoot(b.name);
      if (rootA === 'task_template' && rootB === 'settings') return -1;
      if (rootA === 'settings' && rootB === 'task_template') return 1;
      return a.name.localeCompare(b.name);
    });

    sortedPermissions.forEach((permission) => {
      const parts = permission.name.split('.');
      const action = parts.pop() || 'access';
      if (!allowedActions.includes(action)) {
        return;
      }
      actionSet.add(action);
      let path = parts.length > 0 ? parts : ['misc'];
      if (path[0] === 'dispatch') {
        path = ['projects', 'dispatch', ...path.slice(1)];
      }
      let currentMap = rootMap;
      let currentNode: PermissionNode | null = null;

      path.forEach((segment, index) => {
        const key = path.slice(0, index + 1).join('.');
        let node = currentMap.get(key);
        if (!node) {
          node = {
            id: key,
            label: getNodeLabel(key, toLabel(segment)),
            depth: index,
            actionMap: {},
            children: [],
          };
          currentMap.set(key, node);
          if (currentNode) {
            currentNode.children.push(node);
          }
        }
        currentNode = node;
        if (!(currentNode as any).__childMap) {
          Object.defineProperty(currentNode, '__childMap', {
            value: new Map<string, PermissionNode>(),
            enumerable: false,
            configurable: false,
          });
        }
        currentMap = (currentNode as any).__childMap as Map<string, PermissionNode>;
      });

      const leafNode = currentNode as PermissionNode | null;
      if (leafNode) {
        leafNode.actionMap[action] = permission;
      }
    });

    const actionOrder = ['view', 'create', 'update', 'verify', 'delete'];
    const actions = Array.from(actionSet).sort((a, b) => {
      const aIndex = actionOrder.indexOf(a);
      const bIndex = actionOrder.indexOf(b);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });
    const orderList = [
      'customer',
      'customer.personal',
      'customer.personal.details',
      'customer.personal.family',
      'customer.personal.signature',
      'customer.personal.engagement',
      'customer.personal.business',
      'customer.personal.assets',
      'customer.personal.timeline',
      'customer.business',
      'customer.business.details',
      'customer.business.address',
      'customer.business.nature',
      'customer.business.financial',
      'customer.business.employers',
      'customer.business.company-chop',
      'customer.business.engagement',
      'customer.business.directors',
      'customer.business.members',
      'customer.business.timeline',
      'calendar',
      'files',
      'projects',
      'projects.reports',
      'projects.tasks',
      'projects.tasks.details',
      'projects.tasks.details-plan',
      'projects.tasks.actual',
      'projects.tasks.checklist',
      'projects.tasks.draft',
      'projects.tasks.comment',
      'projects.tasks.review',
      'projects.tasks.actions',
      'projects.letters',
      'projects.letters.details',
      'projects.letters.checklist',
      'projects.letters.letter',
      'projects.letters.comment',
      'projects.letters.review',
      'projects.letters.actions',
      'projects.dispatch',
      'projects.dispatch.details',
      'projects.dispatch.details-collect-return-date',
      'projects.dispatch.details-location',
      'projects.dispatch.checklist',
      'projects.dispatch.photo',
      'projects.dispatch.comment',
      'projects.dispatch.review',
      'projects.dispatch.actions',
      'billing',
      'billing.quotations',
      'billing.quotations.customer-information',
      'billing.quotations.quotation-details',
      'billing.quotations.task-information',
      'billing.quotations.actions',
      'billing.invoices',
      'billing.invoices.customer-information',
      'billing.invoices.invoice-details',
      'billing.invoices.task-information',
      'billing.invoices.payments',
      'task_template',
      'settings',
    ];
    const orderMap = new Map(orderList.map((key, index) => [key, index]));
    const getOrder = (id: string) => {
      const direct = orderMap.get(id);
      if (direct != null) return direct;
      return 10000 + orderMap.size + id.localeCompare('');
    };

    const tree = Array.from(rootMap.values()).sort((a, b) => getOrder(a.id) - getOrder(b.id));
    const normalize = (nodes: PermissionNode[], depth: number) => {
      nodes.forEach((node) => {
        node.depth = depth;
        node.children.sort((a, b) => getOrder(a.id) - getOrder(b.id));
        normalize(node.children, depth + 1);
      });
    };
    normalize(tree, 0);

    return { actions, tree };
  }, [permissions]);

  const columnsCount = permissionMatrix.actions.length || 1;

  const permissionCascade = useMemo(() => {
    const byId = new Map<number, { nodeId: string; action: string }>();
    const subtreeIds = new Map<string, Map<string, number[]>>();

    const mergeMap = (target: Map<string, number[]>, source: Map<string, number[]>) => {
      source.forEach((ids, action) => {
        const existing = target.get(action) ?? [];
        target.set(action, existing.concat(ids));
      });
    };

    const build = (node: PermissionNode): Map<string, number[]> => {
      const acc = new Map<string, number[]>();

      Object.entries(node.actionMap).forEach(([action, permission]) => {
        byId.set(permission.id, { nodeId: node.id, action });
        acc.set(action, [permission.id]);
      });

      node.children.forEach((child) => {
        const childMap = build(child);
        mergeMap(acc, childMap);
      });

      subtreeIds.set(node.id, acc);
      return acc;
    };

    permissionMatrix.tree.forEach(build);

    return { byId, subtreeIds };
  }, [permissionMatrix.tree]);

  const isOwnerRole = selectedRole?.name === 'owner';
  useEffect(() => {
    setEditingEnabled(false);
  }, [selectedRole?.id]);
  const hasChanges = useMemo(() => {
    const current = new Set(selectedPermissionIds);
    const original = new Set(selectedRole?.permissions?.map((permission) => permission.id) ?? []);
    if (current.size !== original.size) return true;
    const currentIds = Array.from(current);
    for (let index = 0; index < currentIds.length; index += 1) {
      const id = currentIds[index];
      if (!original.has(id)) return true;
    }
    return false;
  }, [selectedPermissionIds, selectedRole]);

  const togglePermission = (permissionId: number, checked: boolean) => {
    setSelectedPermissionIds((prev) => {
      const next = new Set(prev);
      const meta = permissionCascade.byId.get(permissionId);
      if (checked) {
        if (meta) {
          const ids = permissionCascade.subtreeIds.get(meta.nodeId)?.get(meta.action) ?? [permissionId];
          ids.forEach((id) => next.add(id));
        } else {
          next.add(permissionId);
        }
      } else {
        if (meta) {
          const ids = permissionCascade.subtreeIds.get(meta.nodeId)?.get(meta.action) ?? [permissionId];
          ids.forEach((id) => next.delete(id));
        } else {
          next.delete(permissionId);
        }
      }
      return Array.from(next);
    });
  };

  const handleSave = async () => {
    if (!selectedRole) return;
    if (selectedRole.name === 'owner') {
      message.info('Owner has full access by default.');
      return;
    }
    setSaving(true);
    try {
      await roleService.updatePermissions(selectedRole.id, selectedPermissionIds);
      message.success('Role permissions updated');
      await fetchData();
    } catch {
      message.error('Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  if (loading && roles.length === 0) {
    return (
      <div className="roles-page">
        <Spin />
      </div>
    );
  }

  return (
    <div className="roles-page">
      <div className="roles-header">
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Roles
          </Title>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            onClick={() => setEditingEnabled((prev) => !prev)}
            disabled={!selectedRole || isOwnerRole}
          >
            {editingEnabled ? 'Cancel' : 'Edit'}
          </Button>
          <Button
            type="primary"
            onClick={handleSave}
            loading={saving}
            disabled={!selectedRole || isOwnerRole || !hasChanges || !editingEnabled}
          >
            Save
          </Button>
        </div>
      </div>

      <div className="roles-layout">
        <div className="roles-panel roles-list">
          <div className="roles-panel-title">
            <span>Roles</span>
            <Tag>{roles.length}</Tag>
          </div>
          {roles.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No roles" />
          ) : (
            <div className="roles-list-items">
              {roles.map((role) => {
                const isActive = role.id === selectedRoleId;
                const permissionCount = role.permissions?.length ?? 0;
                return (
                  <button
                    key={role.id}
                    type="button"
                    className={`roles-list-item${isActive ? ' active' : ''}`}
                    onClick={() => {
                      setSelectedRoleId(role.id);
                      if (role.name === 'owner') {
                        message.info('Owner has full access by default.');
                      }
                    }}
                  >
                    <div>
                      <div className="roles-list-name">{toLabel(role.name)}</div>
                      <div className="roles-list-meta">{permissionCount} permissions</div>
                    </div>
                    <span className="roles-list-chevron">›</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="roles-panel roles-permissions">
          <div className="roles-panel-title">
            <span>Permissions</span>
            {selectedRole ? <Tag>{toLabel(selectedRole.name)}</Tag> : null}
          </div>
          {!selectedRole ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select a role" />
          ) : (
            <div className="roles-permission-table" style={{ ['--roles-cols' as any]: columnsCount }}>
              <div className="roles-permission-row roles-permission-header">
                <div className="roles-permission-cell roles-permission-module">Module</div>
                {permissionMatrix.actions.map((action) => (
                  <div key={action} className="roles-permission-cell">
                    {actionLabel(action)}
                  </div>
                ))}
              </div>
              {(() => {
                const rows: React.ReactNode[] = [];
                const renderNode = (node: PermissionNode) => {
                  const hasChildren = node.children.length > 0;
                  const canToggle = hasChildren && node.depth >= 1;
                  const isCollapsed = canToggle && collapsedKeys.has(node.id);
                  const isExpanded = canToggle && !isCollapsed;
                  rows.push(
                    <div
                      key={node.id}
                      className={`roles-permission-row${hasChildren ? ' is-group' : ''}${isExpanded ? ' is-expanded' : ''} depth-${node.depth}`}
                      style={{ ['--roles-depth' as any]: node.depth }}
                    >
                      <div className="roles-permission-cell roles-permission-module">
                        {canToggle ? (
                          <button
                            type="button"
                            className="roles-permission-toggle"
                            onClick={() => {
                              setCollapsedKeys((prev) => {
                                const next = new Set(prev);
                                if (next.has(node.id)) {
                                  next.delete(node.id);
                                } else {
                                  next.add(node.id);
                                }
                                return next;
                              });
                            }}
                          >
                            <span className={`roles-permission-arrow${isCollapsed ? '' : ' open'}`}>›</span>
                            <span className="roles-permission-label">{node.label}</span>
                          </button>
                        ) : (
                          <span className="roles-permission-label">{node.label}</span>
                        )}
                      </div>
                      {permissionMatrix.actions.map((action) => {
                        const permission = node.actionMap[action];
                        return (
                          <div key={`${node.id}-${action}`} className="roles-permission-cell">
                            {permission ? (
                              <Checkbox
                                checked={selectedPermissionIds.includes(permission.id)}
                                onChange={(event) => togglePermission(permission.id, event.target.checked)}
                                disabled={isOwnerRole || !editingEnabled}
                              />
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  );

                  if (!canToggle || !isCollapsed) {
                    node.children.forEach(renderNode);
                  }
                };

                permissionMatrix.tree.forEach(renderNode);
                return rows;
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
